import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Printer, CaretLeft, CaretRight, FloppyDisk, Eye } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { wibNow } from "@/lib/time";
import { PageHeader } from "@/components/PageHeader";
import { PrintPreviewModal } from "@/components/PrintPreviewModal";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const MON = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const MON_FULL = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const KELAS_ORDER = ["TK B-1", "TK B-2", "TK B-3", "TK A-1", "TK A-2", "TK A-3", "KB"];
const WEEKDAYS = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat"];

function mondayOf(d) {
  const x = new Date(d);
  const day = x.getDay();
  x.setDate(x.getDate() + (day === 0 ? -6 : 1 - day));
  x.setHours(0, 0, 0, 0);
  return x;
}
const dstr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function cellStyle(rec, dateStr, todayStr) {
  if (rec && rec.status === "Izin") return { cls: "bg-stone-200 text-stone-500", txt: "Izin" };
  if (rec && rec.time_in) {
    const [h, m] = rec.time_in.split(".").map(Number);
    const mins = h * 60 + m;
    const t = rec.time_in.replace(".", ":");
    if (mins <= 7 * 60 + 10) return { cls: "bg-green-100 text-green-700", txt: t };
    if (mins <= 7 * 60 + 30) return { cls: "bg-yellow-100 text-yellow-700", txt: t };
    return { cls: "bg-red-100 text-red-600", txt: t };
  }
  if (dateStr > todayStr) return { cls: "bg-white text-stone-300", txt: "" };
  return { cls: "bg-stone-100 text-stone-400", txt: "-" };
}

export default function AdminGuruAttendance() {
  const navigate = useNavigate();
  const now = wibNow();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState([]);
  const [notes, setNotes] = useState({});
  const { user } = useAuth();
  const logoSrc = user?.school === "sdqu" ? "/sdqu_logo.png" : "/tk_logo.png";
  const [showPreview, setShowPreview] = useState(false);

  const [weekOffset, setWeekOffset] = useState(0);
  const [weekRows, setWeekRows] = useState([]);

  const loadNotes = () => api.get("/teacher/notes").then((r) => setNotes(r.data)).catch(() => {});
  const agnPrev = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  const agnNext = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };
  useEffect(() => { api.get(`/teacher/attendance/report?month=${month}&year=${year}`).then((r) => setData(r.data)); }, [month, year]);
  useEffect(() => { loadNotes(); }, []);

  // Weekly table dates (Mon-Fri)
  const weekStart = mondayOf(wibNow());
  weekStart.setDate(weekStart.getDate() + weekOffset * 7);
  const weekDates = [0, 1, 2, 3, 4].map((i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });
  const todayStr = dstr(wibNow());

  useEffect(() => {
    const monthsNeeded = [];
    weekDates.forEach((d) => { const k = `${d.getFullYear()}-${d.getMonth() + 1}`; if (!monthsNeeded.includes(k)) monthsNeeded.push(k); });
    Promise.all(monthsNeeded.map((k) => { const [y, m] = k.split("-"); return api.get(`/teacher/attendance/report?month=${m}&year=${y}`).then((r) => r.data); }))
      .then((results) => {
        const map = {};
        results.forEach((list) => list.forEach((g) => {
          if (!map[g.teacher_id]) map[g.teacher_id] = { teacher_id: g.teacher_id, name: g.name, kelas: g.kelas, recs: {}, piket: {} };
          (g.records || []).forEach((rec) => { map[g.teacher_id].recs[rec.date] = rec; });
          Object.assign(map[g.teacher_id].piket, g.piket || {});
        }));
        const rows = Object.values(map).sort((a, b) => {
          const ia = KELAS_ORDER.indexOf((a.kelas || "").trim()); const ib = KELAS_ORDER.indexOf((b.kelas || "").trim());
          if (ia !== ib) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
          return (a.name || "").localeCompare(b.name || "");
        });
        setWeekRows(rows);
      }).catch(() => setWeekRows([]));
  }, [weekOffset]); // eslint-disable-line

  const rangeLabel = `${weekDates[0].getDate()} ${MON[weekDates[0].getMonth()]} – ${weekDates[4].getDate()} ${MON[weekDates[4].getMonth()]} ${weekDates[4].getFullYear()}`;
  const monthlyRows = [...data].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const dash = (n) => (n > 0 ? n : "–");
  const exportAccumulation = async (period) => {
    try {
      const qs = period === "week" ? `period=week&start=${dstr(weekStart)}` : `period=month&month=${month}&year=${year}`;
      const res = await api.get(`/admin/export/teacher-accumulation.xlsx?${qs}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = period === "week" ? `Akumulasi_Minggu_${dstr(weekStart)}.xlsx` : `Akumulasi_${MON[month - 1]}_${year}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("File Excel diunduh");
    } catch (e) { toast.error("Gagal export"); }
  };

  return (
    <div>
      <PageHeader title="Laporan Kehadiran Guru" />
      <div className="px-5 pt-4">
        {/* Weekly table */}
        <div className="bg-white rounded-3xl border border-stone-200/60 shadow-sm p-4 mb-6" data-testid="weekly-table-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-stone-800 text-sm">Tabel Kehadiran Mingguan</h3>
            <div className="flex items-center gap-1.5">
              <button data-testid="week-prev" onClick={() => setWeekOffset((w) => w - 1)} className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center active:scale-90 transition-transform"><CaretLeft size={16} weight="bold" /></button>
              <button data-testid="week-next" onClick={() => setWeekOffset((w) => Math.min(0, w + 1))} disabled={weekOffset >= 0}
                className={`w-8 h-8 rounded-full flex items-center justify-center active:scale-90 transition-transform ${weekOffset >= 0 ? "bg-stone-50 text-stone-300" : "bg-stone-100"}`}><CaretRight size={16} weight="bold" /></button>
            </div>
          </div>
          <p className="text-xs text-stone-500 mb-3" data-testid="week-range">{rangeLabel}{weekOffset === 0 ? " (minggu ini)" : ""}</p>

          <div className="overflow-x-auto no-scrollbar -mx-1">
            <table className="w-full border-collapse text-xs rounded-xl overflow-hidden" data-testid="weekly-table">
              <thead>
                <tr className="bg-teal-800 text-white">
                  <th className="sticky left-0 bg-teal-800 z-10 text-left font-bold px-2 py-2 min-w-[120px]">Nama Guru</th>
                  {weekDates.map((d, i) => (
                    <th key={i} className="font-bold px-2 py-2 text-center min-w-[64px]">{WEEKDAYS[i]}<br /><span className="text-teal-100 font-semibold">{d.getDate()}/{d.getMonth() + 1}</span></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {weekRows.map((g, ri) => (
                  <tr key={g.teacher_id} data-testid={`week-row-${g.teacher_id}`} className={ri % 2 === 0 ? "bg-teal-50/60" : "bg-white"}>
                    <td className={`sticky left-0 z-10 px-2 py-2 border-t border-teal-100 ${ri % 2 === 0 ? "bg-teal-50/60" : "bg-white"}`}>
                      <p className="font-semibold text-stone-800 leading-tight">{g.name}</p>
                      <p className="text-[11px] text-stone-400">{g.kelas || "—"}</p>
                    </td>
                    {weekDates.map((d, i) => {
                      const ds = dstr(d);
                      const st = cellStyle(g.recs[ds], ds, todayStr);
                      const pk = g.piket && g.piket[ds];
                      return <td key={i} className="px-1 py-1.5 text-center border-t border-teal-100"><span data-testid={pk ? `piket-cell-${g.teacher_id}-${ds}` : undefined} title={pk ? (pk === "tepat_waktu" ? "Piket tepat waktu" : "Piket terlambat") : undefined} className={`inline-block w-full py-1.5 font-bold ${st.cls} ${pk ? "rounded-full ring-[3px] ring-orange-500 ring-offset-1" : "rounded-lg"}`}>{st.txt}</span></td>;
                    })}
                  </tr>
                ))}
                {weekRows.length === 0 && <tr><td colSpan={6} className="text-center text-stone-400 py-6">Belum ada data.</td></tr>}
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3 text-[11px]">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-200 inline-block" /> Tepat waktu (≤07:10)</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-200 inline-block" /> 07:11–07:30</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-200 inline-block" /> Terlambat (&gt;07:30)</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-stone-200 inline-block" /> Izin / Tidak hadir</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full ring-[3px] ring-orange-500 inline-block" /> Piket pagi</span>
          </div>
        </div>

        {/* Monthly accumulation (all teachers, alphabetical) */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-heading font-bold text-stone-800 text-sm" data-testid="akumulasi-bulanan-title">Akumulasi Kehadiran Guru Bulanan</h3>
            <p className="text-xs text-stone-500">{MON_FULL[month - 1]} {year}</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="agn-prev-month" onClick={agnPrev} className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center active:scale-95 transition-transform"><CaretLeft size={16} weight="bold" /></button>
            <button data-testid="agn-next-month" onClick={agnNext} className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center active:scale-95 transition-transform"><CaretRight size={16} weight="bold" /></button>
          </div>
        </div>
        <div className="bg-white rounded-3xl border border-stone-200/60 shadow-sm p-3 mb-6 overflow-x-auto no-scrollbar" data-testid="akumulasi-bulanan-card">
          <table className="w-full border-collapse text-xs" data-testid="akumulasi-bulanan-table">
            <thead>
              <tr className="bg-teal-800 text-white">
                <th className="px-2 py-2 text-center font-bold">No</th>
                <th className="px-2 py-2 text-left font-bold min-w-[100px]">Nama Guru</th>
                <th className="px-1.5 py-2 text-center font-bold">Kehadiran</th>
                <th className="px-1.5 py-2 text-center font-bold">Izin</th>
                <th className="px-1.5 py-2 text-center font-bold">Sakit</th>
                <th className="px-1.5 py-2 text-center font-bold">Piket Pagi</th>
                <th className="px-1.5 py-2 text-center font-bold">Piket Sore</th>
              </tr>
            </thead>
            <tbody>
              {monthlyRows.map((g, i) => {
                const kehadiran = (g.hadir || 0) + (g.terlambat || 0);
                const izin = Math.max(0, (g.izin || 0) - (g.sakit || 0));
                const pagi = Object.keys(g.piket || {}).length;
                const sore = Object.keys(g.piket_sore || {}).length;
                return (
                  <tr key={g.teacher_id} data-testid={`akumulasi-row-${g.teacher_id}`} className={i % 2 === 0 ? "bg-teal-50/40" : "bg-white"}>
                    <td className="px-2 py-2 text-center text-stone-500 border-t border-teal-100">{i + 1}</td>
                    <td className="px-2 py-2 border-t border-teal-100"><p className="font-semibold text-stone-800 leading-tight">{g.name}</p><p className="text-[10px] text-stone-400">{g.kelas || "—"}</p></td>
                    <td className="px-1.5 py-2 text-center font-bold text-green-700 border-t border-teal-100">{dash(kehadiran)}</td>
                    <td className="px-1.5 py-2 text-center text-blue-600 border-t border-teal-100">{dash(izin)}</td>
                    <td className="px-1.5 py-2 text-center text-amber-600 border-t border-teal-100">{dash(g.sakit || 0)}</td>
                    <td className="px-1.5 py-2 text-center text-orange-600 border-t border-teal-100">{dash(pagi)}</td>
                    <td className="px-1.5 py-2 text-center text-orange-600 border-t border-teal-100">{dash(sore)}</td>
                  </tr>
                );
              })}
              {monthlyRows.length === 0 && <tr><td colSpan={7} className="text-center text-stone-400 py-6">Belum ada data.</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Export akumulasi: preview/PDF + pilihan per minggu / per bulan */}
        <div className="mb-6 no-print space-y-2">
          <button data-testid="akumulasi-preview-btn" onClick={() => setShowPreview(true)}
            className="w-full border-2 border-stone-300 text-stone-700 font-semibold py-2.5 rounded-full text-xs flex items-center justify-center gap-1.5 active:scale-95 transition-transform">
            <Eye size={16} weight="bold" /> Preview & Export PDF (1 halaman)
          </button>
          <div className="flex gap-2">
            <button data-testid="export-akumulasi-minggu" onClick={() => exportAccumulation("week")}
              className="flex-1 border-2 border-primary text-primary font-semibold py-2.5 rounded-full text-xs flex items-center justify-center gap-1.5 active:scale-95 transition-transform">
              <FloppyDisk size={16} weight="bold" /> Export Minggu Ini
            </button>
            <button data-testid="export-akumulasi-bulan" onClick={() => exportAccumulation("month")}
              className="flex-1 bg-primary text-white font-semibold py-2.5 rounded-full text-xs flex items-center justify-center gap-1.5 active:scale-95 transition-transform">
              <FloppyDisk size={16} weight="bold" /> Export {MON_FULL[month - 1]}
            </button>
          </div>
        </div>

        <PrintPreviewModal open={showPreview} onClose={() => setShowPreview(false)} logoSrc={logoSrc}
          title="Akumulasi Kehadiran Guru Bulanan" subtitle={`${MON_FULL[month - 1]} ${year}`}
          fileName={`Akumulasi_Kehadiran_Guru_${MON[month - 1]}_${year}.pdf`}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["No", "Nama Guru", "Kelas", "Kehadiran", "Izin", "Sakit", "Piket Pagi", "Piket Sore"].map((h, idx) => (
                  <th key={idx} style={{ border: "1px solid #0f766e", padding: "9px 10px", background: "#115E59", color: "#ffffff", fontSize: 14, fontWeight: 700, textAlign: idx === 1 ? "left" : "center" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {monthlyRows.map((g, i) => {
                const kehadiran = (g.hadir || 0) + (g.terlambat || 0);
                const izin = Math.max(0, (g.izin || 0) - (g.sakit || 0));
                const pagi = Object.keys(g.piket || {}).length;
                const sore = Object.keys(g.piket_sore || {}).length;
                const td = { border: "1px solid #dddddd", padding: "8px 10px", fontSize: 14, textAlign: "center", color: "#1c1917" };
                return (
                  <tr key={g.teacher_id}>
                    <td style={td}>{i + 1}</td>
                    <td style={{ ...td, textAlign: "left", fontWeight: 600 }}>{g.name}</td>
                    <td style={td}>{g.kelas || "—"}</td>
                    <td style={{ ...td, color: "#15803d", fontWeight: 700 }}>{dash(kehadiran)}</td>
                    <td style={{ ...td, color: "#2563eb" }}>{dash(izin)}</td>
                    <td style={{ ...td, color: "#d97706" }}>{dash(g.sakit || 0)}</td>
                    <td style={{ ...td, color: "#ea580c" }}>{dash(pagi)}</td>
                    <td style={{ ...td, color: "#ea580c" }}>{dash(sore)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </PrintPreviewModal>

        {/* Monthly accumulation */}
        <h3 className="font-heading font-bold text-stone-800 text-sm mb-2">Akumulasi Bulanan per Guru</h3>
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-4">
          {MON.map((m, i) => (
            <button key={m} data-testid={`agn-bulan-${i + 1}`} onClick={() => setMonth(i + 1)}
              className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm font-semibold ${month === i + 1 ? "bg-primary text-white" : "bg-white border border-stone-200 text-stone-500"}`}>{m}</button>
          ))}
        </div>

        <div className="space-y-5">
          {KELAS_ORDER.map((kls) => {
            const items = [...data].filter((g) => (g.kelas || "").trim() === kls).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
            if (items.length === 0) return null;
            return (
              <div key={kls} data-testid={`kelas-group-${kls}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold text-primary bg-primary/10 px-2.5 py-1 rounded-full">{kls}</span>
                  <span className="text-[11px] text-stone-400">{items.length} guru</span>
                  <div className="flex-1 h-px bg-stone-200/70" />
                </div>
                <div className="space-y-3">
                  {items.map((g) => <GuruCard key={g.teacher_id} g={g} month={month} year={year} navigate={navigate} note={notes[g.teacher_id]} onSaved={loadNotes} />)}
                </div>
              </div>
            );
          })}
          {(() => {
            const others = [...data].filter((g) => !KELAS_ORDER.includes((g.kelas || "").trim())).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
            if (others.length === 0) return null;
            return (
              <div data-testid="kelas-group-lainnya">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold text-stone-500 bg-stone-100 px-2.5 py-1 rounded-full">Lainnya</span>
                  <span className="text-[11px] text-stone-400">{others.length} guru</span>
                  <div className="flex-1 h-px bg-stone-200/70" />
                </div>
                <div className="space-y-3">
                  {others.map((g) => <GuruCard key={g.teacher_id} g={g} month={month} year={year} navigate={navigate} note={notes[g.teacher_id]} onSaved={loadNotes} />)}
                </div>
              </div>
            );
          })()}
          {data.length === 0 && <p className="text-center text-stone-400 mt-8">Belum ada data guru.</p>}
        </div>

        <button data-testid="agn-export" onClick={async () => {
          try {
            const res = await api.get(`/admin/export/teacher-attendance.xlsx?month=${month}&year=${year}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a"); a.href = url; a.download = `Kehadiran_Guru_${MON[month - 1]}_${year}.xlsx`;
            document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
            toast.success("File Excel diunduh");
          } catch (e) { toast.error("Gagal export"); }
        }} className="no-print w-full border-2 border-primary text-primary font-semibold py-3.5 rounded-full mt-3 flex items-center justify-center gap-2 active:scale-95 transition-transform">
          <FloppyDisk size={18} weight="bold" /> Export Excel Kehadiran ({MON[month - 1]} {year})
        </button>

        <button data-testid="agn-print" onClick={() => window.print()} className="no-print w-full bg-primary text-white font-semibold py-3.5 rounded-full mt-5 flex items-center justify-center gap-2 active:scale-95 transition-transform">
          <Printer size={18} weight="fill" /> Cetak / Export Laporan
        </button>
      </div>
    </div>
  );
}

function GuruCard({ g, month, year, navigate, note, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(note?.note || "");
  useEffect(() => { setText(note?.note || ""); }, [note]);

  const save = async () => {
    try { await api.put(`/teacher/notes/${g.teacher_id}`, { note: text }); toast.success("Catatan disimpan"); setEditing(false); onSaved && onSaved(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid={`guru-att-${g.teacher_id}`} className="bg-white rounded-2xl border border-stone-200/60 p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-semibold text-stone-800">{g.name}</p>
          <p className="text-stone-400 text-xs">{g.code} • {g.kelas || "—"} • {MON[month - 1]} {year}</p>
        </div>
        <button onClick={() => navigate(`/kelola/laporan-guru/${g.teacher_id}`)} className="text-primary text-xs font-semibold" data-testid={`detail-${g.teacher_id}`}>
          Detail
        </button>
      </div>
      <div className="grid grid-cols-4 gap-2 mt-3">
        <Cell v={g.hadir} l="Hadir" c="text-green-600" />
        <Cell v={g.terlambat} l="Terlambat" c="text-amber-600" />
        <Cell v={g.izin} l="Izin" c="text-sky-600" />
        <Cell v={g.tidak_hadir} l="Tdk Hadir" c="text-red-500" />
      </div>

      {/* Catatan Admin */}
      <div className="mt-3 border-t border-stone-100 pt-3" data-testid={`note-block-${g.teacher_id}`}>
        {!editing ? (
          <div>
            {note?.note ? (
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-2.5">
                <p className="text-[11px] font-bold text-amber-700">Catatan Admin</p>
                <p className="text-sm text-stone-700">{note.note}</p>
                {note.reply && <p className="text-xs text-stone-500 mt-1.5">↳ Jawaban guru: {note.reply}</p>}
              </div>
            ) : null}
            <button data-testid={`note-edit-${g.teacher_id}`} onClick={() => setEditing(true)} className="text-primary text-xs font-semibold mt-2">
              {note?.note ? "Ubah Catatan" : "+ Tambah Catatan Admin"}
            </button>
          </div>
        ) : (
          <div>
            <textarea data-testid={`note-input-${g.teacher_id}`} value={text} onChange={(e) => setText(e.target.value)} rows={2}
              placeholder="Tulis catatan untuk guru..." className="w-full bg-stone-50 border border-stone-200 rounded-xl py-2 px-3 text-sm outline-none focus:border-primary resize-none mb-2" />
            <div className="flex gap-2">
              <button data-testid={`note-save-${g.teacher_id}`} onClick={save} className="flex-1 bg-primary text-white font-semibold py-2 rounded-full text-sm flex items-center justify-center gap-1.5 active:scale-95 transition-transform"><FloppyDisk size={15} weight="bold" /> Simpan</button>
              <button onClick={() => { setEditing(false); setText(note?.note || ""); }} className="px-4 bg-stone-100 text-stone-600 font-semibold py-2 rounded-full text-sm">Batal</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Cell({ v, l, c, hint }) {
  return (
    <div className="bg-stone-50 rounded-xl p-2 text-center">
      <p className={`font-heading font-bold text-xl ${c}`}>{v}</p>
      <p className="text-[10px] text-stone-400">{hint || l}</p>
    </div>
  );
}
